import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'
import PublishModal from '../components/PublishModal'
import { useAuth } from '../contexts/AuthContext'
import {
  getSkill,
  getHistory,
  archiveSkill,
  listAllProjects,
  addSkillToProject,
  removeSkillFromProject,
} from '../api'

// ─── Types ──────────────────────────────────────────────────────────────────

interface SkillData {
  name: string
  description: string
  version: string
  status: string
  content: string
  tags: string[]
  file_type: string
  project_id: string | null
  user_role: string
  share_mode: string
  id: string
}

interface CommitEntry {
  message: string
  committed_date?: string
  author?: string
  hexsha?: string
  version?: string
  source?: string
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function ownerFromFrontmatter(content: string): string {
  const m = content.match(/^owner:\s*(.+)$/m)
  return m ? m[1].trim() : ''
}

function statusBadge(status: string): React.CSSProperties {
  if (status === 'production') return { background: 'var(--success-bg)', color: 'var(--success-text)' }
  if (status === 'staging')    return { background: 'var(--warning-bg)', color: 'var(--warning-text)' }
  if (status === 'archived')   return { background: 'rgba(239,68,68,0.08)', color: '#ef4444' }
  return { background: 'var(--surface2)', color: 'var(--text3)' }
}

function timeAgo(iso?: string): string {
  if (!iso) return ''
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const FIELD_LABEL: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, color: 'var(--text3)',
  textTransform: 'uppercase', letterSpacing: '0.5px',
  marginBottom: 4, display: 'block',
}

const FIELD_VALUE: React.CSSProperties = {
  fontSize: 13, color: 'var(--text1)', lineHeight: 1.5,
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function EditorPage() {
  const { name } = useParams<{ name: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [skill, setSkill] = useState<SkillData | null>(null)
  const [history, setHistory] = useState<CommitEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(true)
  const [error, setError] = useState('')

  // Move modal
  const [moveGroups, setMoveGroups] = useState<{ team_id: string; team_name: string; projects: { id: string; name: string; skill_count: number }[] }[]>([])
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [moveLoading, setMoveLoading] = useState(false)
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null)
  const [moveToast, setMoveToast] = useState('')

  // Publish
  const [showPublishModal, setShowPublishModal] = useState(false)

  // Archive
  const [archiving, setArchiving] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)

  // ─── Load skill ────────────────────────────────────────────────────────────

  const loadSkill = useCallback(async () => {
    if (!name) return
    setLoading(true)
    try {
      const r = await getSkill(name)
      setSkill(r.data)
      setCurrentProjectId(r.data.project_id || null)
    } catch {
      setError('Failed to load skill')
    } finally {
      setLoading(false)
    }
  }, [name])

  useEffect(() => { loadSkill() }, [loadSkill])

  useEffect(() => {
    if (!name) return
    setHistoryLoading(true)
    getHistory(name)
      .then(r => setHistory(Array.isArray(r.data) ? r.data : []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false))
    listAllProjects()
      .then(r => setMoveGroups(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
  }, [name])

  // ─── Actions ───────────────────────────────────────────────────────────────

  const handleMoveTo = async (targetProjectId: string | null) => {
    if (!name) return
    setMoveLoading(true)
    try {
      if (targetProjectId === null) {
        if (currentProjectId) await removeSkillFromProject(currentProjectId, name)
        setCurrentProjectId(null)
      } else {
        await addSkillToProject(targetProjectId, name)
        setCurrentProjectId(targetProjectId)
      }
      setShowMoveModal(false)
      const targetName = targetProjectId
        ? moveGroups.flatMap(g => g.projects).find(p => p.id === targetProjectId)?.name || ''
        : 'Unassigned'
      setMoveToast(`Moved to ${targetName}`)
      setTimeout(() => setMoveToast(''), 4000)
    } catch {
      setMoveToast('Move failed')
      setTimeout(() => setMoveToast(''), 4000)
    } finally {
      setMoveLoading(false)
    }
  }

  const handleArchive = async () => {
    if (!name) return
    setArchiving(true)
    try {
      await archiveSkill(name)
      navigate(-1)
    } catch {
      setError('Archive failed')
    } finally {
      setArchiving(false)
      setShowArchiveConfirm(false)
    }
  }

  const handleDownload = () => {
    if (!skill) return
    const blob = new Blob([skill.content], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${name}.md`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ─── Render guards ─────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
    </div>
  )

  if (error || !skill) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 12 }}>{error || 'Skill not found'}</div>
        <button className="btn-ghost" style={{ padding: '7px 16px', fontSize: 13 }} onClick={() => navigate(-1)}>← Back</button>
      </div>
    </div>
  )

  const owner = ownerFromFrontmatter(skill.content)
  const currentProject = moveGroups.flatMap(g => g.projects).find(p => p.id === currentProjectId)
  const canPublish = skill.file_type !== 'document' && skill.status !== 'archived'
  const canArchive = skill.status !== 'archived'

  // ─── Main layout ──────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text1)', paddingBottom: 48 }}>

      {/* Nav */}
      <nav style={{
        height: 52, display: 'flex', alignItems: 'center',
        padding: '0 20px', gap: 12,
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <Logo size="sm" linkTo="/" />
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text2)', fontSize: 13, fontFamily: 'inherit', padding: '4px 8px', borderRadius: 6 }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <span style={{ color: 'var(--border2)', fontSize: 13 }}>›</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {skill.name}
        </span>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => setShowMoveModal(true)}
            style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', fontFamily: 'inherit' }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--text3)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
          >
            Move
          </button>
          {canPublish && (
            <button
              onClick={() => setShowPublishModal(true)}
              style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'var(--purple)', color: '#fff', fontFamily: 'inherit' }}
              onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
            >
              Publish
            </button>
          )}
          {canArchive && (
            <button
              onClick={() => setShowArchiveConfirm(true)}
              disabled={archiving}
              style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', fontFamily: 'inherit' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)' }}
            >
              {archiving ? 'Archiving…' : 'Archive'}
            </button>
          )}
          <button
            onClick={handleDownload}
            style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5 }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--text3)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            .md
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

        {/* ── Metadata card ────────────────────────────────────────────── */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text3)', marginBottom: 20 }}>
            Skill details
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            <div>
              <span style={FIELD_LABEL}>Name</span>
              <span style={FIELD_VALUE}>{skill.name}</span>
            </div>

            {skill.description && (
              <div>
                <span style={FIELD_LABEL}>Description</span>
                <span style={{ ...FIELD_VALUE, color: 'var(--text2)' }}>{skill.description}</span>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <span style={FIELD_LABEL}>Version</span>
                <span style={{ ...FIELD_VALUE, fontFamily: 'monospace', fontSize: 12 }}>{skill.version}</span>
              </div>
              <div>
                <span style={FIELD_LABEL}>Status</span>
                <span style={{
                  display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 8px',
                  borderRadius: 4, textTransform: 'capitalize',
                  ...statusBadge(skill.status),
                }}>
                  {skill.status}
                </span>
              </div>
            </div>

            {owner && (
              <div>
                <span style={FIELD_LABEL}>Owner</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--purple)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                    {owner[0]?.toUpperCase() ?? 'U'}
                  </div>
                  <span style={FIELD_VALUE}>{owner}</span>
                </div>
              </div>
            )}

            <div>
              <span style={FIELD_LABEL}>Project</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 12, padding: '2px 10px', borderRadius: 10,
                  background: currentProjectId ? 'rgba(129,44,196,0.10)' : 'var(--surface2)',
                  color: currentProjectId ? 'var(--purple)' : 'var(--text3)',
                  fontWeight: 500,
                }}>
                  {currentProject?.name ?? (currentProjectId ? '…' : 'Unassigned')}
                </span>
                <button
                  onClick={() => setShowMoveModal(true)}
                  style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text3)', cursor: 'pointer', fontFamily: 'inherit' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'var(--text1)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--text3)')}
                >
                  Move
                </button>
              </div>
            </div>

            {skill.tags && skill.tags.length > 0 && (
              <div>
                <span style={FIELD_LABEL}>Tags</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {skill.tags.map(t => (
                    <span key={t} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--surface2)', color: 'var(--text2)', border: '1px solid var(--border)' }}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* ── Version history ───────────────────────────────────────────── */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text3)', marginBottom: 16 }}>
            Version history
          </div>

          {historyLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text3)' }}>
              <div className="spinner" /> Loading history…
            </div>
          ) : history.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text3)' }}>No history yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {history.map((c, i) => (
                <div key={i} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: i === 0 ? 'rgba(253,183,20,0.04)' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 8 }}>
                      {c.message || `Version ${c.version || ''}`}
                    </span>
                    {i === 0 && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(253,183,20,0.15)', color: '#b38000', flexShrink: 0 }}>
                        LATEST
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: 'var(--text3)' }}>
                    {c.hexsha && (
                      <span style={{ fontFamily: 'monospace', background: 'var(--surface2)', padding: '1px 5px', borderRadius: 3, fontSize: 10 }}>
                        {c.hexsha.substring(0, 7)}
                      </span>
                    )}
                    {c.version && <span>{c.version}</span>}
                    {c.committed_date && <span>{timeAgo(c.committed_date)}</span>}
                    {c.author && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--purple)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700 }}>
                          {c.author[0]?.toUpperCase() ?? '?'}
                        </div>
                        <span>{c.author}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Move modal ─────────────────────────────────────────────────────── */}
      {showMoveModal && (
        <div className="modal-overlay" onClick={() => setShowMoveModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--text1)' }}>Move to project</div>
              <button onClick={() => setShowMoveModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>&times;</button>
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {currentProjectId && (
                <>
                  <div
                    onClick={() => !moveLoading && handleMoveTo(null)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, marginBottom: 8, cursor: moveLoading ? 'not-allowed' : 'pointer', background: 'var(--surface2)', border: '1px dashed var(--border)', transition: 'border-color 0.1s' }}
                    onMouseEnter={e => { if (!moveLoading) e.currentTarget.style.borderColor = 'var(--text3)' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
                    </svg>
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text2)' }}>Unassigned</span>
                  </div>
                  <div style={{ height: 1, background: 'var(--border)', marginBottom: 8 }} />
                </>
              )}
              {moveGroups.length === 0 ? (
                <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>No projects found</div>
              ) : moveGroups.map(group => (
                <div key={group.team_id} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5, paddingLeft: 2 }}>
                    {group.team_name}
                  </div>
                  {group.projects.map(p => {
                    const isCurrent = p.id === currentProjectId
                    return (
                      <div key={p.id}
                        onClick={() => { if (!isCurrent && !moveLoading) handleMoveTo(p.id) }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderRadius: 8, marginBottom: 3, cursor: isCurrent ? 'default' : moveLoading ? 'not-allowed' : 'pointer', background: 'var(--surface2)', border: '1px solid var(--border)', opacity: isCurrent ? 0.4 : 1, transition: 'border-color 0.1s' }}
                        onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.borderColor = '#fdb714' }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)' }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)' }}>{p.name}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{p.skill_count} skills</span>
                          {isCurrent && <span style={{ fontSize: 10, color: 'var(--text3)', fontStyle: 'italic' }}>current</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Archive confirm ────────────────────────────────────────────────── */}
      {showArchiveConfirm && (
        <div className="modal-overlay" onClick={() => setShowArchiveConfirm(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 15, fontWeight: 700, marginBottom: 8, color: 'var(--text1)' }}>Archive skill?</div>
            <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 20 }}>
              <strong>{skill.name}</strong> will be archived and removed from active lists. You can still access it via the Archived filter.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowArchiveConfirm(false)} className="btn-ghost" style={{ padding: '7px 16px', fontSize: 12 }}>Cancel</button>
              <button onClick={handleArchive} style={{ padding: '7px 16px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: '#ef4444', color: '#fff', fontFamily: 'inherit' }}>
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Publish modal ──────────────────────────────────────────────────── */}
      {showPublishModal && name && (
        <PublishModal
          skillId={skill.id || name}
          destinations={{ openwebui: true, claude: false }}
          onClose={() => setShowPublishModal(false)}
          onPublished={() => {
            setSkill(s => s ? { ...s, status: 'production' } : s)
            setShowPublishModal(false)
          }}
        />
      )}

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      {moveToast && (
        <div style={{ position: 'fixed', bottom: 40, left: '50%', transform: 'translateX(-50%)', padding: '9px 16px', borderRadius: 8, fontSize: 12, fontWeight: 500, zIndex: 400, background: 'var(--text1)', color: 'var(--bg)', whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
          {moveToast}
        </div>
      )}
    </div>
  )
}
